// src/Pages/ApiKeys/ApiKeyStats.tsx

import type React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@tremor/react";
import {
  KeyIcon,
  CheckCircleIcon,
  ClockIcon,
  ActivityIcon,
} from "lucide-react";
import type { ApiKeyStats, ApiKeyRecentUsage } from "@/types/apiKeys";

interface ApiKeyStatsProps {
  stats: ApiKeyStats | null;
  recentUsage: ApiKeyRecentUsage[];
  loading?: boolean;
}

const ApiKeyStats: React.FC<ApiKeyStatsProps> = ({
  stats,
  recentUsage,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded animate-pulse w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      title: "Συνολικά Keys",
      value: stats.total_keys,
      icon: KeyIcon,
      color: "blue",
      description: "Συνολικός αριθμός API keys",
    },
    {
      title: "Ενεργά Keys",
      value: stats.active_keys,
      icon: CheckCircleIcon,
      color: "green",
      description: `${Math.round((stats.active_keys / stats.total_keys) * 100)}% του συνόλου`,
    },
    {
      title: "Χρησιμοποιημένα",
      value: stats.used_keys,
      icon: ActivityIcon,
      color: "purple",
      description: "Keys που έχουν χρησιμοποιηθεί",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <Icon className={`h-4 w-4 text-${stat.color}-500`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {recentUsage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center space-x-2">
              <ClockIcon className="h-4 w-4" />
              <span>Πρόσφατη Χρήση</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentUsage.slice(0, 5).map((usage, index) => {
                const lastUsedDate = new Date(usage.last_used_at);
                const now = new Date();
                const diffMs = now.getTime() - lastUsedDate.getTime();
                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                
                let timeAgo;
                if (diffHours === 0) {
                  const diffMinutes = Math.floor(diffMs / (1000 * 60));
                  timeAgo = `Πριν ${diffMinutes} λεπτά`;
                } else if (diffHours < 24) {
                  timeAgo = `Πριν ${diffHours} ώρες`;
                } else {
                  const diffDays = Math.floor(diffHours / 24);
                  timeAgo = `Πριν ${diffDays} ημέρ${diffDays === 1 ? "α" : "ες"}`;
                }
                
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b last:border-0"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{usage.name}</span>
                      <span className="text-xs text-gray-500">
                        {usage.org_name}
                      </span>
                    </div>
                    <Badge color={diffHours < 1 ? "green" : diffHours < 24 ? "blue" : "gray"}>
                      {timeAgo}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApiKeyStats;